import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';

/**
 * The self-service password-reset request, `/forgot`.
 *
 * The answer is the same whether or not the address has an account, so the form
 * cannot be used to find out which addresses exist — the confirmation, shown only
 * on a 2xx, never changes. Too many requests earns a plain "try later", and a
 * server error a neutral retry (both about the request, not any account); neither
 * claims a message is on its way, so a broken endpoint cannot masquerade as
 * success. It is a public page: it renders signed out and links back to sign-in.
 */

const GENERIC = 'If that address has an account, a message is on its way.';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Only a real 2xx counts as sent; any thrown error keeps the form up with a
      // neutral message, so the success text tracks an actual accepted request.
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 429) {
        setError('You have tried this too many times. Please wait a while and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-lg font-semibold tracking-tight text-strong">Reset your password</h1>
          <p className="mt-1 text-sm text-muted">
            Enter your email and we will send a link if the address has an account.
          </p>
        </header>

        <Card className="p-6">
          {sent ? (
            <>
              <Alert tone="success">{GENERIC}</Alert>
              <p className="mt-4 text-sm text-muted">
                You will still need your authenticator to sign in. The link expires in an hour.
              </p>
            </>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              {error && <Alert tone="warning">{error}</Alert>}
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                  autoFocus
                  disabled={busy}
                />
              </Field>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          )}
        </Card>

        <p className="mt-4 text-center text-xs">
          <a href="/" className="text-brand-700 underline underline-offset-2">
            Back to sign in
          </a>
        </p>
      </div>
    </div>
  );
}
